import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateForCountry } from '../utils/countries';
import { Download, Check, X, MonitorCheck, BadgeAlert } from 'lucide-react';
import './ScreenshotModal.css';

const APP_URL = process.env.APP_URL || 'https://ruxengine.com';

function ScreenshotModal({ check, onClose, user }) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';

  const isCfProtected = check.error_message?.startsWith('CF_PROTECTED');

  const screenshotUrl = check.screenshot_path
    ? `${APP_URL}/screenshots/${check.screenshot_path}`
    : null;

  const handleDownload = () => {
    if (screenshotUrl) {
      const link = document.createElement('a');
      link.href = screenshotUrl;
      link.download = check.screenshot_path || 'screenshot.webp';
      link.click();
    }
  };

  const renderLayerBadge = (status, label) => {
    if (!status) return null;
    const isOk = status === 'ok';
    return (
      <div className="info-row">
        <strong>{label}:</strong>
        <span className={`badge ${isOk ? '' : ''}`}>
          {isOk ? <MonitorCheck size={18} strokeWidth={2} color="#10b981" /> : <BadgeAlert size={18} strokeWidth={2} color="#ef4444" />} {/*status.toUpperCase()*/}
        </span>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{t('modal.errorDetail')}</h2>
            <p className="modal-subtitle">
              {formatDateForCountry(check.checked_at, user?.country, {}, currentLanguage)}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Sol: Temel Durumlar */}
          <div className="info-panel">
            {renderLayerBadge(check.http_status, 'HTTP')}
            {renderLayerBadge(check.dns_status, 'DNS')}
            {renderLayerBadge(check.tcp_status, 'TCP')}
            {renderLayerBadge(check.ssl_status, 'SSL')}
          </div>

          {/* Sağ: Ekstra Detaylar */}
          <div className="info-panel">
            <div className="info-row">
              <strong>{t('check.status')}:</strong>
              {isCfProtected ? (
                <span className="badge warning">
                  <BadgeAlert size={18} strokeWidth={2} color="#f59e0b" />
                  {t('error.cfProtected')}
                </span>
              ) : (
                <span className={`badge ${check.status === 'success' ? 'success' : 'danger'}`}>
                  {check.status === 'error' ? (
                    <>
                      <X size={18} strokeWidth={2} color="#ef4444" />
                      {t('status.error')}
                    </>
                  ) : (
                    <>
                      <Check size={18} strokeWidth={2} color="#10b981" />
                      {t('status.success')}
                    </>
                  )}
                </span>
              )}
            </div>

            {check.status_code != null && (
              <div className="info-row">
                <strong>{t('check.statusCode')}:</strong>
                <span>{check.status_code}</span>
              </div>
            )}
            {check.redirect_count > 0 && (
              <div className="info-row">
                <strong>{t('check.redirectCount')}:</strong>
                <span className={check.redirect_count > 2 ? 'badge warning' : ''}>
                  {check.redirect_count}
                </span>
              </div>
            )}

            {check.response_time && (
              <div className="info-row">
                <strong>{t('check.responseTime')}:</strong>
                <span>{check.response_time} ms</span>
              </div>
            )}

            {check.error_message && (
              <div className="info-row">
                <strong>{t('check.errorMessage')}:</strong>
                <span className={isCfProtected ? 'badge-cf-protected' : 'error-text'}>
                  {isCfProtected ? t('error.cfProtected') : check.error_message}
                </span>
              </div>
            )}

            {check.final_url && check.final_url !== check.domain_url && (
              <div className="info-row">
                <strong>{t('check.finalUrl')}:</strong>
                <span style={{ fontSize: '0.875rem', textAlign: 'right' }}>
                  {check.final_url}
                </span>
              </div>
            )}
          </div>

          {/* Screenshot — CF korumalı sitelerde gösterme */}
          {!isCfProtected && (
          <div className="screenshot-section">
            <span className="screenshot-label show-image">{t('screenshot.showImage')}</span>
            {screenshotUrl ? (
              <div className="screenshot-container">
                <a title="Show Image" href={screenshotUrl} target="_blank" rel="noopener noreferrer" className="screenshot-link">
                  <img
                    src={screenshotUrl}
                    alt="Error Screenshot"
                    className="screenshot-image"
                    loading="lazy"
                  />
                </a>
              </div>
            ) : (
              <div className="no-screenshot">
                {t('screenshot.notAvailable')}
              </div>
            )}
          </div>
          )}
        </div>

        <div className="modal-actions">
          {screenshotUrl && !isCfProtected && (
            <button className="btn-secondary" onClick={handleDownload} style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
              <Download size={16} strokeWidth={2} color="#000000" /> {t('action.download')}

            </button>
          )}
          <button className="btn-primary" onClick={onClose}>
            {t('action.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScreenshotModal;
